import { Fragment, useState } from "react"
import Card from "@/components/card"
import Link from "@/components/link"
import ListDropdown from "@/components/list-dropdown"
import { RPS_RISK_AXES, RpsRiskAxis } from "@/entities/population/model/rps-risk"
import { EstablishmentScanResult } from "../model/establishment.types"

//merge : groupes a fusionner ; open : un risque par ligne, a ouvrir ; rps : un risque par ligne, a passer en RPS
export type ScanResultKind = "merge" | "open" | "rps"

interface Props {
  title: string
  description: string
  kind: ScanResultKind
  results: EstablishmentScanResult[]
  busyId: string | null //Resultat dont l'action (fusion, passage en RPS) est en cours
  onMerge: (id: string) => void
  onMoveToRps: (id: string, riskId: string, axis: RpsRiskAxis) => void
  onOpenRisk: (id: string) => void
}

const AXES = RPS_RISK_AXES.map((axis) => ({ id: axis.id, name: axis.label }))

export default function EstablishmentScanResultCard({
  title,
  description,
  kind,
  results,
  busyId,
  onMerge,
  onMoveToRps,
  onOpenRisk
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false)
  //Axe choisi par risque, sinon celui propose par la revue
  const [axes, setAxes] = useState<Record<string, RpsRiskAxis>>({})
  const isMultiple = results.length > 1
  const isGroup = kind === "merge"

  function renderRisk(result: EstablishmentScanResult) {
    const riskId = result.items?.[0]?.id
    return (
      <Link isWithoutMargin className="!inline" onClick={riskId ? () => onOpenRisk(riskId) : undefined}>
        {result.name || "Risque sans nom"}
      </Link>
    )
  }

  function renderRps(result: EstablishmentScanResult) {
    const riskId = result.items?.[0]?.id
    const axis = axes[result.id] ?? result.axis ?? RPS_RISK_AXES[0].id
    const isDisabled = busyId !== null || !riskId
    return (
      <div className="flex flex-col gap-[4px]">
        {renderRisk(result)}
        <div className="flex items-center gap-[10px]">
          <ListDropdown
            isCaretAlwaysVisible
            active={axis}
            value={AXES.find((item) => item.id === axis)?.name}
            values={AXES}
            onSelect={(item) => setAxes({ ...axes, [result.id]: item.id })}
          />
          <Link
            isWithoutMargin
            className={`!inline whitespace-nowrap ${isDisabled ? "opacity-50 !cursor-default" : ""}`}
            onClick={isDisabled ? undefined : () => onMoveToRps(result.id, riskId, axis)}>
            {busyId === result.id ? "En cours…" : "Passer en RPS"}
          </Link>
        </div>
      </div>
    )
  }

  function renderGroup(result: EstablishmentScanResult) {
    const isMergeDisabled =
      busyId !== null ||
      !result.items ||
      result.items.length < 2 ||
      result.items.some((item) => item.itemType !== "physicalRisk")
    return (
      <>
        <span>{result.name}</span>{" "}
        {result.items?.map(
          (item, index) =>
            item.itemType === "physicalRisk" && (
              <Fragment key={item.id}>
                <Link isWithoutMargin className="!inline" onClick={() => onOpenRisk(item.id)}>
                  {index + 1}
                </Link>{" "}
              </Fragment>
            )
        )}
        <Link
          isWithoutMargin
          className={`!inline whitespace-nowrap ${isMergeDisabled ? "opacity-50 !cursor-default" : ""}`}
          onClick={isMergeDisabled ? undefined : () => onMerge(result.id)}>
          {busyId === result.id ? "Fusion en cours…" : "Fusionner"}
        </Link>
      </>
    )
  }

  return (
    <Card isWithoutPadding className="mb-10">
      <div className="flex flex-col gap-[12px] p-[16px] text-base">
        <h3 className="m-0 text-base font-bold">{title}</h3>
        <div className="grey-t text-sm">{description}</div>
        {results.length === 0 && (
          <div className="grey-t">{isGroup ? "Aucun groupe détecté." : "Aucun risque concerné."}</div>
        )}
        {isMultiple && (
          <div className="flex items-center justify-between gap-[10px]">
            <span className="grey-t">
              {results.length} {isGroup ? "groupes" : "risques"}
            </span>
            <Link isWithoutMargin onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? "Afficher moins" : "Afficher plus"}
            </Link>
          </div>
        )}
        {(!isMultiple || isExpanded) && (
          <ul className="m-0 flex list-none flex-col gap-[16px] p-0">
            {results.map((result) => (
              <li key={result.id}>
                {isGroup ? renderGroup(result) : kind === "rps" ? renderRps(result) : renderRisk(result)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}
